import {
  AssetRecordType,
  createShapeId,
  createBindingId,
  toRichText,
  removeFrame,
} from 'tldraw';
import { syncPinShapes, layerBoardContent } from './syncPinShapes';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import {
  collectAllPinCenters,
  collectOccupiedPinRects,
  collectSelectedPinCenters,
  resolveSuggestPinFallbackPosition,
} from './suggestPinPlacement';
import { resolveDisplayTitle } from './pinTitle';
import {
  SUGGEST_PIN_WIDTH,
  SUGGEST_PIN_MIN_HEIGHT,
} from '../constants/suggestPinLayout';
import { getActivityId } from './autoLayout';
import { separatePinPlacements } from './separatePinPlacements';
import {
  normalizeLogicalFrameId,
  membershipIdFromFrameShape,
  logicalFrameIdFromShapeId,
  syncPinFrameMembership,
} from './frameMembership';
import { deleteUserPins } from './deleteUserPins';
import { enqueueDigestDeleteFromStore } from './boardConstraintsDigest';
import { arrowLabelMeta, boardLinkShapeProps, normalizeArrowLabelText } from './arrowTerminalLabels';
import { flushBoardDocumentOps } from './persistBoardDocumentOps';
import { unmountShapesThen } from './pendingShapeUnmount';
import {
  doneGapFrameId,
  doneGapFrameLabel,
  frameNameMatchesDoneKey,
  siblingActivityIdsForDoneKey,
} from './boardContextRecommendations';
import { fitFrameToMemberPlacements, DONE_GAP_FRAME_PADDING } from './doneGapFrameLayout';
const PIN_SHAPE_PREFIX = 'pin:';
const PIN_TEXT_GAP = 12;

/**
 * Delete frames without cascade-deleting their children.
 * tldraw's editor.deleteShapes() removes descendants; connectors/text/drawings
 * that were drag-reparented into a frame would otherwise vanish with the frame.
 */
function deleteFramesPreservingChildren(editor, frameIds = []) {
  if (!editor || !frameIds?.length) return;
  const ids = frameIds.filter(id => {
    const shape = editor.getShape(id);
    return shape?.type === 'frame';
  });
  if (!ids.length) return;
  const priorSelection = editor.getSelectedShapeIds?.() || [];
  try {
    // removeFrame reparents children with correct page coords, then deletes the frame.
    removeFrame(editor, ids);
  } catch {
    // Fallback: reparent children to the page, then delete the empty frames.
    const pageId = editor.getCurrentPageId();
    for (const frameId of ids) {
      const childIds = editor.getSortedChildIdsForParent?.(frameId) || [];
      if (childIds.length) {
        try {
          editor.reparentShapes(childIds, pageId);
        } catch {
          /* optional */
        }
      }
    }
    unmountShapesThen(ids, () => {
      editor.deleteShapes(ids);
    });
  }
  // removeFrame selects former children — restore prior selection for AI batches.
  try {
    const stillSelected = priorSelection.filter(id => editor.getShape(id));
    editor.setSelectedShapes?.(stillSelected);
  } catch {
    /* optional */
  }
}

function normalizeBoardFrame(frame) {
  if (!frame?.id) return null;
  const id = normalizeLogicalFrameId(frame.id);
  if (!id) return null;
  return { ...frame, id };
}

function normalizeBoardFrames(frames = []) {
  return (frames || []).map(normalizeBoardFrame).filter(Boolean);
}

function normalizePlacementFrameIds(placements = []) {
  return (placements || []).map(placement => {
    if (!placement) return placement;
    const frameId =
      placement.frameId == null ? null : normalizeLogicalFrameId(placement.frameId);
    return { ...placement, frameId };
  });
}

function resolvePinShapeId(editor, activityId) {
  const id = createShapeId(`${PIN_SHAPE_PREFIX}${activityId}`);
  if (editor.getShape(id)) return id;
  const match = editor
    .getCurrentPageShapes()
    .find(
      s =>
        s.type === 'pin-card' && String(s.meta?.activityId) === String(activityId)
    );
  return match?.id || id;
}

function resolveTargetShapeId(editor, rawId) {
  if (!editor || rawId == null) return null;
  const id = String(rawId);

  if (id.startsWith('text:')) {
    const activityId = id.slice('text:'.length);
    const textShapeId = createShapeId(`ai-text-${activityId}`);
    if (editor.getShape(textShapeId)) return textShapeId;
  }

  const candidates = [
    resolvePinShapeId(editor, id.replace(/^pin:/, '')),
    id,
    id.startsWith('shape:') ? id : `shape:${id}`,
    createShapeId(id.replace(/^shape:/, '')),
  ];
  for (const candidate of candidates) {
    if (candidate && editor.getShape(candidate)) return candidate;
  }
  return null;
}

function shapeCenter(shape) {
  const w = shape.props?.w ?? 300;
  const h = shape.props?.h ?? 400;
  return { x: shape.x + w / 2, y: shape.y + h / 2 };
}

function createArrowBetweenShapes(editor, fromShapeId, toShapeId, labelSpec) {
  const fromShape = editor.getShape(fromShapeId);
  const toShape = editor.getShape(toShapeId);
  if (!fromShape || !toShape) return null;

  const label = typeof labelSpec === 'string'
    ? normalizeArrowLabelText(labelSpec)
    : normalizeArrowLabelText(
      labelSpec?.label,
      labelSpec?.endLabel,
      labelSpec?.startLabel
    );

  const from = shapeCenter(fromShape);
  const to = shapeCenter(toShape);
  const arrowId = createShapeId(`ai-arrow-${fromShapeId}-${toShapeId}`);

  if (editor.getShape(arrowId)) return arrowId;

  editor.createShapes([
    {
      id: arrowId,
      type: 'arrow',
      x: from.x,
      y: from.y,
      meta: arrowLabelMeta({}, label),
      props: boardLinkShapeProps({
        start: { x: 0, y: 0 },
        end: { x: to.x - from.x, y: to.y - from.y },
        color: 'blue',
        size: 'm',
      }),
    },
  ]);

  try {
    editor.createBindings([
      {
        id: createBindingId(),
        type: 'arrow',
        fromId: arrowId,
        toId: fromShapeId,
        props: {
          terminal: 'start',
          isExact: false,
          normalizedAnchor: { x: 0.5, y: 0.5 },
          isPrecise: false,
        },
      },
      {
        id: createBindingId(),
        type: 'arrow',
        fromId: arrowId,
        toId: toShapeId,
        props: {
          terminal: 'end',
          isExact: false,
          normalizedAnchor: { x: 0.5, y: 0.5 },
          isPrecise: false,
        },
      },
    ]);
  } catch {
    /* bindings optional */
  }

  return arrowId;
}

function createFrameShape(editor, frame) {
  const logicalId = normalizeLogicalFrameId(frame.id);
  if (!logicalId) return null;

  // Stock tldraw frames keep their native shape id as the membership key.
  if (logicalId.startsWith('shape:')) {
    if (!editor.getShape(logicalId)) return null;
    editor.updateShape({
      id: logicalId,
      type: 'frame',
      x: frame.x,
      y: frame.y,
      props: { w: frame.w, h: frame.h, name: frame.label || 'Section' },
      meta: { boardFrameId: logicalId },
    });
    return logicalId;
  }

  const frameId = createShapeId(`frame-${logicalId}`);
  const frameMeta = { boardFrameId: logicalId };
  if (editor.getShape(frameId)) {
    editor.updateShape({
      id: frameId,
      type: 'frame',
      x: frame.x,
      y: frame.y,
      props: { w: frame.w, h: frame.h, name: frame.label || 'Section' },
      meta: frameMeta,
    });
    return frameId;
  }

  editor.createShape({
    id: frameId,
    type: 'frame',
    x: frame.x,
    y: frame.y,
    props: {
      w: frame.w,
      h: frame.h,
      name: frame.label || 'Section',
    },
    meta: frameMeta,
  });
  return frameId;
}

function resolveFrameShapeId(editor, frame) {
  const logicalId = normalizeLogicalFrameId(frame.id);
  if (!logicalId) return null;
  if (logicalId.startsWith('shape:')) {
    return editor.getShape(logicalId) ? logicalId : null;
  }
  const frameId = createShapeId(`frame-${logicalId}`);
  return editor.getShape(frameId) ? frameId : null;
}

/**
 * Drop previous AI organize frames that are not in the latest frame list so pins
 * are not left associated with orphaned empty frames.
 * Uses removeFrame (not deleteShapes) so connectors/annotations inside frames survive.
 */
function removeStaleAiFrames(editor, keepFrames = []) {
  if (!editor) return;
  const keep = new Set(
    keepFrames.map(frame => normalizeLogicalFrameId(frame?.id)).filter(Boolean).map(String)
  );
  const pageId = editor.getCurrentPageId();
  const toDelete = [];

  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'frame' || shape.parentId !== pageId) continue;
    const isAiFrame =
      Boolean(shape.meta?.boardFrameId) || Boolean(logicalFrameIdFromShapeId(shape.id));
    if (!isAiFrame) continue;
    const membershipId = membershipIdFromFrameShape(shape);
    if (membershipId && keep.has(String(membershipId))) continue;
    toDelete.push(shape.id);
  }

  if (toDelete.length) deleteFramesPreservingChildren(editor, toDelete);
}

function frameExistsForMembershipId(editor, frameId) {
  if (frameId == null || frameId === '') return false;
  const key = String(frameId);
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'frame') continue;
    if (String(membershipIdFromFrameShape(shape)) === key) return true;
  }
  return false;
}

/**
 * Ensure pin-card meta.frameId matches placement.frameId after organize/layout.
 * Belonging is soft membership (not tldraw parentId); frame move-follow depends on this.
 * When placement.frameId is missing or does not match a live frame, derive from geometry
 * unless assignFromGeometry is false (pin-add: keep unframed).
 */
function applyPinFrameMembershipFromPlacements(
  editor,
  placements = [],
  { assignFromGeometry = true } = {}
) {
  if (!editor || !placements?.length) return placements || [];

  return placements.map(placement => {
    const activityId = placement?.activityId != null ? String(placement.activityId) : null;
    if (!activityId) return placement;

    const shapeId = resolvePinShapeId(editor, activityId);
    const shape = editor.getShape(shapeId);
    if (!shape) return placement;

    let nextFrameId =
      placement.frameId == null ? null : normalizeLogicalFrameId(placement.frameId);
    if (nextFrameId != null && !frameExistsForMembershipId(editor, nextFrameId)) {
      nextFrameId = null;
    }

    if (nextFrameId == null && assignFromGeometry) {
      nextFrameId = syncPinFrameMembership(editor, shape.id) ?? null;
    } else if ((shape.meta?.frameId || null) !== nextFrameId) {
      editor.updateShape({
        id: shape.id,
        type: shape.type,
        meta: {
          ...shape.meta,
          frameId: nextFrameId,
        },
      });
    }

    if ((placement.frameId || null) === (nextFrameId || null) && placement.activityId === activityId) {
      return placement;
    }
    return { ...placement, activityId, frameId: nextFrameId };
  });
}

function shapePageCenter(editor, shape) {
  const bounds = editor.getShapePageBounds(shape);
  if (!bounds) {
    return {
      x: shape.x + (shape.props?.w ?? 0) / 2,
      y: shape.y + (shape.props?.h ?? 0) / 2,
    };
  }
  return { x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 };
}

function pointInFrame(x, y, frame) {
  return (
    x >= frame.x
    && x <= frame.x + frame.w
    && y >= frame.y
    && y <= frame.y + frame.h
  );
}

function reparentCanvasAnnotationsToFrames(editor, frames) {
  if (!frames?.length) return;

  const frameShapeById = new Map(
    frames
      .map(frame => [frame.id, resolveFrameShapeId(editor, frame)])
      .filter(([, shapeId]) => shapeId)
  );

  const skipTypes = new Set(['pin-card', 'suggest-pin', 'conversation-pin', 'frame', 'arrow']);
  const pageId = editor.getCurrentPageId();

  for (const shape of editor.getCurrentPageShapes()) {
    if (skipTypes.has(shape.type)) continue;
    if (shape.parentId !== pageId) continue;

    const center = shapePageCenter(editor, shape);
    for (const frame of frames) {
      if (!pointInFrame(center.x, center.y, frame)) continue;
      const parentId = frameShapeById.get(frame.id);
      if (!parentId || !editor.getShape(shape.id)) break;
      try {
        editor.reparentShapes([shape.id], parentId);
      } catch {
        /* optional */
      }
      break;
    }
  }
}

function mergePlacements(existing = [], updates = []) {
  const byId = new Map((existing || []).map(p => [String(p.activityId), p]));
  for (const update of updates) {
    const key = String(update.activityId);
    byId.set(key, { ...byId.get(key), ...update, activityId: key });
  }
  return [...byId.values()];
}

function createSuggestPinShape(editor, suggestPin, index, placementContext = {}) {
  const slug = encodeURIComponent(suggestPin.url).slice(0, 40);
  const shapeId = createShapeId(`suggest-${slug}-${index}`);
  const existing = editor.getShape(shapeId);
  if (existing) {
    if (Math.abs((existing.props?.w ?? 0) - SUGGEST_PIN_WIDTH) > 2) {
      editor.updateShape({
        id: shapeId,
        type: 'suggest-pin',
        props: { w: SUGGEST_PIN_WIDTH },
      });
    }
    return shapeId;
  }

  const position = resolveSuggestPinFallbackPosition(suggestPin, index, {
    ...placementContext,
    pinSize: { w: SUGGEST_PIN_WIDTH, h: SUGGEST_PIN_MIN_HEIGHT },
  });
  const title = resolveDisplayTitle(suggestPin.title, suggestPin.url, {
    snippet: suggestPin.snippet,
  });

  editor.createShape({
    id: shapeId,
    type: 'suggest-pin',
    x: position.x,
    y: position.y,
    props: {
      w: SUGGEST_PIN_WIDTH,
      h: SUGGEST_PIN_MIN_HEIGHT,
      url: suggestPin.url,
      title,
      snippet: suggestPin.snippet || '',
      imageUrl: suggestPin.imageUrl || '',
      reason: suggestPin.reason || '',
      searchQuery: suggestPin.searchQuery || '',
    },
  });

  placementContext.occupiedRects?.push({
    x: position.x,
    y: position.y,
    w: SUGGEST_PIN_WIDTH,
    h: SUGGEST_PIN_MIN_HEIGHT,
  });

  return shapeId;
}

/**
 * Place a tldraw text shape directly under a pin, matching pin width.
 * Free-floating texts use x/y (and optional w) instead of a pin anchor.
 */
function createTextBelowPin(editor, textEntry) {
  const content = String(textEntry.text || '').trim();
  if (!content) return null;

  const size = textEntry.size === 'm' || textEntry.size === 'l' ? textEntry.size : 's';
  const activityId = textEntry.activityId != null ? String(textEntry.activityId) : null;
  const hasCoords =
    Number.isFinite(Number(textEntry.x)) && Number.isFinite(Number(textEntry.y));

  if (activityId && !hasCoords) {
    const pinShapeId = resolvePinShapeId(editor, activityId);
    const pinShape = editor.getShape(pinShapeId);
    if (!pinShape) return null;

    const pinW = pinShape.props?.w ?? 300;
    const pinH = pinShape.props?.h ?? 400;
    const shapeId = createShapeId(`ai-text-${activityId}`);
    const props = {
      color: 'black',
      size,
      w: pinW,
      font: 'sans',
      textAlign: 'start',
      autoSize: false,
      scale: 1,
      richText: toRichText(content),
    };

    const next = {
      id: shapeId,
      type: 'text',
      x: pinShape.x,
      y: pinShape.y + pinH + PIN_TEXT_GAP,
      props,
      meta: { activityId, aiPinInsight: true },
    };
    if (editor.getShape(shapeId)) {
      editor.updateShape(next);
    } else {
      editor.createShape(next);
    }
    return shapeId;
  }

  if (!hasCoords && !activityId) return null;

  let x = Number(textEntry.x);
  let y = Number(textEntry.y);
  let w = Number.isFinite(Number(textEntry.w)) ? Number(textEntry.w) : 240;

  if (activityId && hasCoords) {
    // Optional absolute override while still tagging the pin.
  } else if (activityId) {
    const pinShape = editor.getShape(resolvePinShapeId(editor, activityId));
    if (pinShape) {
      x = pinShape.x;
      y = pinShape.y + (pinShape.props?.h ?? 400) + PIN_TEXT_GAP;
      w = pinShape.props?.w ?? w;
    }
  }

  const slug = activityId
    ? `ai-text-${activityId}`
    : `ai-text-free-${Math.round(x)}-${Math.round(y)}-${content.slice(0, 24).replace(/\W+/g, '-')}`;
  const shapeId = createShapeId(slug);
  const props = {
    color: 'black',
    size,
    w,
    font: 'sans',
    textAlign: 'start',
    autoSize: false,
    scale: 1,
    richText: toRichText(content),
  };
  const next = {
    id: shapeId,
    type: 'text',
    x,
    y,
    props,
    meta: activityId ? { activityId, aiGenerated: true } : { aiGenerated: true },
  };
  if (editor.getShape(shapeId)) {
    editor.updateShape(next);
  } else {
    editor.createShape(next);
  }
  return shapeId;
}

function createImageFromUrl(editor, imageEntry, index = 0) {
  const url = String(imageEntry?.url || '').trim();
  if (!url || !editor) return null;

  const w = Number.isFinite(Number(imageEntry.w)) ? Number(imageEntry.w) : 400;
  const h = Number.isFinite(Number(imageEntry.h)) ? Number(imageEntry.h) : 300;
  const x = Number.isFinite(Number(imageEntry.x)) ? Number(imageEntry.x) : 80 + index * 24;
  const y = Number.isFinite(Number(imageEntry.y)) ? Number(imageEntry.y) : 80 + index * 24;
  const mimeType = String(imageEntry.mimeType || 'image/jpeg');
  const name = String(imageEntry.name || 'image').slice(0, 120);

  const assetId = AssetRecordType.createId();
  const shapeId = createShapeId(`ai-image-${Date.now()}-${index}`);

  editor.createAssets([
    {
      id: assetId,
      type: 'image',
      typeName: 'asset',
      props: {
        name,
        src: url,
        w,
        h,
        mimeType,
        isAnimated: mimeType.includes('gif'),
      },
      meta: { aiGenerated: true, sourceUrl: url },
    },
  ]);

  editor.createShape({
    id: shapeId,
    type: 'image',
    x,
    y,
    props: {
      assetId,
      w,
      h,
      playing: true,
      url: '',
      crop: null,
      flipX: false,
      flipY: false,
    },
    meta: { aiGenerated: true, sourceUrl: url },
  });

  return shapeId;
}

function deleteCanvasShapes(editor, shapeIds = []) {
  if (!editor || !shapeIds?.length) return { deletedIds: [], deletedFrameLogicalIds: [] };
  const resolved = [];
  const frameIds = [];
  const deletedFrameLogicalIds = [];
  for (const rawId of shapeIds) {
    const id = resolveTargetShapeId(editor, rawId);
    if (!id) continue;
    const shape = editor.getShape(id);
    if (!shape) continue;
    // Never delete pin-cards through deleteShapes — use deletePins.
    if (shape.type === 'pin-card') continue;
    if (shape.type === 'frame') {
      const logical = String(shape.id).replace(/^shape:frame-/, '').replace(/^frame-/, '');
      if (logical) deletedFrameLogicalIds.push(logical);
      frameIds.push(id);
      continue;
    }
    resolved.push(id);
  }
  // Frames first via removeFrame so children (arrows/text/draw) are reparented, not deleted.
  if (frameIds.length) deleteFramesPreservingChildren(editor, frameIds);
  if (resolved.length) {
    unmountShapesThen(resolved, () => {
      editor.deleteShapes(resolved);
    });
  }
  return { deletedIds: [...frameIds, ...resolved], deletedFrameLogicalIds };
}

const FRAME_FIT_PADDING = 32;
const DEFAULT_PIN_W = 300;
const DEFAULT_PIN_H = 400;

function placementSize(placement) {
  return {
    w: Number(placement.width) || DEFAULT_PIN_W,
    h: Number(placement.height) || DEFAULT_PIN_H,
  };
}

/**
 * Expand frames so every assigned pin's full rect stays visible inside the frame.
 * Pins stay page-level; membership is meta.frameId + spatial containment.
 */
function fitFramesToPlacements(frames = [], placements = [], padding = FRAME_FIT_PADDING) {
  if (!frames?.length) return frames;

  return frames.map(frame => {
    const frameKey = String(frame.id);
    const members = placements.filter(
      p => p.frameId != null && String(p.frameId) === frameKey
    );
    if (!members.length) return frame;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const placement of members) {
      const { w, h } = placementSize(placement);
      const x = Number(placement.x);
      const y = Number(placement.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    }
    if (!Number.isFinite(minX)) return frame;

    const neededX = minX - padding;
    const neededY = minY - padding;
    const neededRight = maxX + padding;
    const neededBottom = maxY + padding;
    const x = Math.min(Number(frame.x) || 0, neededX);
    const y = Math.min(Number(frame.y) || 0, neededY);
    const right = Math.max((Number(frame.x) || 0) + (Number(frame.w) || 0), neededRight);
    const bottom = Math.max((Number(frame.y) || 0) + (Number(frame.h) || 0), neededBottom);

    return {
      ...frame,
      x,
      y,
      w: Math.max(right - x, Number(frame.w) || 0),
      h: Math.max(bottom - y, Number(frame.h) || 0),
    };
  });
}

function mergeBoardFrames(existing = [], updates = []) {
  if (!updates?.length) return existing || [];
  const byId = new Map((existing || []).map(f => [String(f.id), f]));
  for (const frame of updates) {
    byId.set(String(frame.id), { ...byId.get(String(frame.id)), ...frame });
  }
  return [...byId.values()];
}

/**
 * Apply AI response to the tldraw editor inside a single undo batch.
 */
export async function applyBoardAiActions(
  editor,
  actions,
  {
    activities,
    board,
    updatePlacementsLocal,
    savePlacements,
    saveFrames,
    saveSnapshot,
    deleteActivities,
    /** Only the explicit Place /suggest-pins action may write suggest-pin shapes. */
    allowSuggestPins = false,
    /**
     * When false (pin-add), do not assign frame membership from geometry or AI frameId.
     * Organize / Command keep the default true.
     */
    assignFrameMembership = true,
  } = {}
) {
  if (!editor || !actions) return { summary: actions?.summary };

  const beforeSnapshot = extractBoardDocumentSnapshot(editor);

  const deletePinIds = [...new Set((actions.deletePins || []).map(String).filter(Boolean))];
  let deletedPinIds = [];
  if (deletePinIds.length) {
    // Same cascade as manual pin delete: DB (activity/comment/screenshots) + canvas shapes.
    // Related annotation removes are flushed via /ops at the end (no /snapshot).
    deletedPinIds = await deleteUserPins(editor, deletePinIds, {
      deleteActivities,
      activities,
      board,
      updatePlacementsLocal,
      onDeleted: (deletedIds) => {
        enqueueDigestDeleteFromStore(deletedIds);
      },
    });
  }

  const deletedSet = new Set(deletedPinIds.map(String));
  let workingActivities = activities || [];
  if (deletedSet.size) {
    workingActivities = workingActivities.filter(a => !deletedSet.has(getActivityId(a)));
  }

  let nextBoard = board ? { ...board } : {};
  if (deletedSet.size) {
    const nextPlacements = (nextBoard.placements || []).filter(
      p => !deletedSet.has(String(p.activityId))
    );
    nextBoard = { ...nextBoard, placements: nextPlacements };
  }

  const changedShapeIds = [];
  const suggestPinShapeIds = [];
  const deletedDigestShapeIds = [];

  editor.run(() => {
    editor.markHistoryStoppingPoint('before-ai-action');

    if (actions.deleteShapes?.length) {
      const { deletedIds, deletedFrameLogicalIds } = deleteCanvasShapes(editor, actions.deleteShapes);
      deletedDigestShapeIds.push(...(deletedIds || []), ...(deletedFrameLogicalIds || []));
      if (deletedFrameLogicalIds.length) {
        const removed = new Set(deletedFrameLogicalIds.map(String));
        const nextFrames = (nextBoard.frames || []).filter(f => !removed.has(String(f.id)));
        if (nextFrames.length !== (nextBoard.frames || []).length) {
          nextBoard = { ...nextBoard, frames: nextFrames };
          saveFrames?.(nextFrames);
        }
      }
    }

    if (actions.placements?.length) {
      const normalizedActionFrames = assignFrameMembership
        ? normalizeBoardFrames(actions.frames || [])
        : [];
      const normalizedActionPlacements = assignFrameMembership
        ? normalizePlacementFrameIds(actions.placements)
        : normalizePlacementFrameIds(actions.placements).map(p =>
          (p ? { ...p, frameId: null } : p)
        );
      const baseFrames = normalizedActionFrames.length
        ? normalizedActionFrames
        : normalizeBoardFrames(nextBoard.frames || []);
      const { placements: separatedPlacements, frames: separatedFrames } =
        separatePinPlacements(editor, normalizedActionPlacements, {
          activities: workingActivities,
          frames: assignFrameMembership ? baseFrames : [],
          existingPlacements: normalizePlacementFrameIds(nextBoard.placements || []),
        });

      const mergedPlacements = mergePlacements(nextBoard?.placements, separatedPlacements);
      const placementsTouchFrames = assignFrameMembership
        && separatedPlacements.some(p => p.frameId != null);
      let framesForApply = null;

      if (normalizedActionFrames.length || placementsTouchFrames) {
        framesForApply = fitFramesToPlacements(
          normalizedActionFrames.length
            ? separatedFrames
            : mergeBoardFrames(baseFrames, separatedFrames),
          mergedPlacements
        );
        if (framesForApply.length) {
          if (normalizedActionFrames.length) {
            removeStaleAiFrames(editor, framesForApply);
          }
          for (const frame of framesForApply) {
            const frameShapeId = createFrameShape(editor, frame);
            if (frameShapeId) changedShapeIds.push(frameShapeId);
          }
          nextBoard = {
            ...nextBoard,
            frames: normalizedActionFrames.length
              ? framesForApply
              : mergeBoardFrames(nextBoard.frames, framesForApply),
          };
          saveFrames?.(nextBoard.frames);
        }
      }

      nextBoard = { ...nextBoard, placements: mergedPlacements };
      syncPinShapes(editor, workingActivities, nextBoard);
      const finalPlacements = applyPinFrameMembershipFromPlacements(
        editor,
        mergedPlacements,
        { assignFromGeometry: assignFrameMembership }
      );
      nextBoard = { ...nextBoard, placements: finalPlacements };
      // savePlacements owns local+API; avoid updatePlacementsLocal first (no-op save).
      savePlacements?.(finalPlacements);

      if (framesForApply?.length) {
        reparentCanvasAnnotationsToFrames(editor, framesForApply);
      }

      for (const placement of separatedPlacements) {
        const pinShapeId = resolvePinShapeId(editor, placement.activityId);
        if (editor.getShape(pinShapeId)) changedShapeIds.push(pinShapeId);
      }
    } else if (actions.frames?.length) {
      const fittedFrames = fitFramesToPlacements(
        normalizeBoardFrames(actions.frames),
        normalizePlacementFrameIds(nextBoard.placements || [])
      );
      removeStaleAiFrames(editor, fittedFrames);
      for (const frame of fittedFrames) {
        const frameShapeId = createFrameShape(editor, frame);
        if (frameShapeId) changedShapeIds.push(frameShapeId);
      }
      nextBoard = { ...nextBoard, frames: fittedFrames };
      saveFrames?.(fittedFrames);
      const finalPlacements = applyPinFrameMembershipFromPlacements(
        editor,
        normalizePlacementFrameIds(nextBoard.placements || [])
      );
      nextBoard = { ...nextBoard, placements: finalPlacements };
      savePlacements?.(finalPlacements);
      reparentCanvasAnnotationsToFrames(editor, fittedFrames);
    }

    if (actions.texts?.length) {
      for (const textEntry of actions.texts) {
        const shapeId = createTextBelowPin(editor, textEntry);
        if (shapeId) changedShapeIds.push(shapeId);
      }
    }

    if (assignFrameMembership && actions.arrows?.length) {
      for (const arrow of actions.arrows) {
        const fromId = resolveTargetShapeId(editor, arrow.fromId);
        const toId = resolveTargetShapeId(editor, arrow.toId);
        if (!fromId || !toId) continue;
        const arrowId = createArrowBetweenShapes(editor, fromId, toId, arrow);
        if (arrowId) changedShapeIds.push(arrowId);
      }
    }

    if (actions.images?.length) {
      actions.images.forEach((img, i) => {
        const shapeId = createImageFromUrl(editor, img, i);
        if (shapeId) changedShapeIds.push(shapeId);
      });
    }

    if (allowSuggestPins && actions.suggestPins?.length) {
      const placementContext = {
        selectedCenters: collectSelectedPinCenters(editor),
        allPinCenters: collectAllPinCenters(editor),
        occupiedRects: collectOccupiedPinRects(editor),
        viewportBounds: editor.getViewportPageBounds?.() || null,
      };
      actions.suggestPins.forEach((sp, i) => {
        const shapeId = createSuggestPinShape(editor, sp, i, placementContext);
        if (shapeId) {
          changedShapeIds.push(shapeId);
          suggestPinShapeIds.push({ index: i, shapeId });
        }
      });
    }

    layerBoardContent(editor);
  });

  if (deletedDigestShapeIds.length) {
    enqueueDigestDeleteFromStore([], [...new Set(deletedDigestShapeIds.map(String))]);
  }

  try {
    if (typeof saveSnapshot === 'function') {
      // Ops-only saver when wired from TldrawBoard / toolbar.
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      await flushBoardDocumentOps(editor, beforeSnapshot);
    }
  } catch {
    /* ignore */
  }

  return { summary: actions.summary, deletedPinIds, selectedShapeIds: changedShapeIds, suggestPinShapeIds };
}

export function hasBoardAiMutations(actions, { allowSuggestPins = false } = {}) {
  if (!actions) return false;
  return Boolean(
    actions.placements?.length ||
      actions.frames?.length ||
      actions.arrows?.length ||
      actions.texts?.length ||
      actions.images?.length ||
      actions.deletePins?.length ||
      actions.deleteShapes?.length ||
      (allowSuggestPins && actions.suggestPins?.length)
  );
}

export function removeSuggestPinShape(editor, shapeId, saveSnapshot) {
  if (!editor || !shapeId) return;
  const before = extractBoardDocumentSnapshot(editor);
  unmountShapesThen([shapeId], () => {
    editor.run(() => {
      editor.markHistoryStoppingPoint('before-remove-suggest-pin');
      editor.deleteShape(shapeId);
    });
  });
  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      void flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Manually place one chat search result as a suggest-pin on the canvas.
 */
export function placeSuggestPinFromSource(editor, source, { saveSnapshot } = {}) {
  const url = String(source?.url || '').trim();
  if (!editor || !url) return null;

  const placementContext = {
    // Prefer where the user is looking (chat pin) over a distant board centroid,
    // so the new AI pin does not jump the camera to an empty region of the page.
    selectedCenters: collectSelectedPinCenters(editor),
    allPinCenters: [],
    occupiedRects: collectOccupiedPinRects(editor),
    viewportBounds: editor.getViewportPageBounds?.() || null,
  };

  let shapeId = null;
  const before = extractBoardDocumentSnapshot(editor);
  editor.run(() => {
    editor.markHistoryStoppingPoint('before-pin-chat-source');
    shapeId = createSuggestPinShape(
      editor,
      {
        url,
        title: source.title || '',
        snippet: source.snippet || '',
        imageUrl: source.imageUrl || '',
        reason: source.reason || 'Pinned from chat search',
        searchQuery: source.searchQuery || '',
        x: source.x,
        y: source.y,
      },
      Date.now() % 100000,
      placementContext
    );
    layerBoardContent(editor);
  });

  if (shapeId) {
    try {
      editor.select(shapeId);
      editor.zoomToSelection({ animation: { duration: 220 } });
    } catch {
      /* ignore */
    }
    try {
      if (typeof saveSnapshot === 'function') {
        saveSnapshot(extractBoardDocumentSnapshot(editor));
      } else {
        void flushBoardDocumentOps(editor, before);
      }
    } catch {
      /* ignore */
    }
  }

  return shapeId;
}

/**
 * Collect board.frames plus live editor frames so we never drop existing AI frames
 * when upserting a done-gap frame via actions.frames.
 */
function collectBoardAndEditorFrames(editor, board) {
  const byId = new Map();
  for (const frame of board?.frames || []) {
    const id = normalizeLogicalFrameId(frame?.id);
    if (!id) continue;
    byId.set(String(id), {
      id,
      x: Number(frame.x) || 0,
      y: Number(frame.y) || 0,
      w: Number(frame.w) || 0,
      h: Number(frame.h) || 0,
      label: String(frame.label || ''),
    });
  }
  if (!editor?.getCurrentPageShapes) return [...byId.values()];
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'frame') continue;
    const id = membershipIdFromFrameShape(shape);
    if (!id) continue;
    const key = String(id);
    if (byId.has(key)) continue;
    byId.set(key, {
      id,
      x: shape.x,
      y: shape.y,
      w: Number(shape.props?.w) || 0,
      h: Number(shape.props?.h) || 0,
      label: String(shape.props?.name || ''),
    });
  }
  return [...byId.values()];
}

/**
 * Find an existing frame for this missing part (stable done-gap id or matching name).
 */
function findDoneGapFrame(editor, board, doneKey) {
  const logicalId = doneGapFrameId(doneKey);
  const frames = collectBoardAndEditorFrames(editor, board);
  const byId = frames.find(frame => String(frame.id) === logicalId);
  if (byId) {
    return {
      ...byId,
      id: logicalId,
      label: byId.label || doneGapFrameLabel(doneKey),
    };
  }
  const byName = frames.find(frame => frameNameMatchesDoneKey(frame.label, doneKey));
  if (byName) {
    return {
      ...byName,
      label: byName.label || doneGapFrameLabel(doneKey),
    };
  }
  return null;
}

/**
 * After soft pin-case marks a gap done: put the pin (and same-gap peers) inside a
 * frame named for that missing part. Creates the frame when none exists yet.
 * Pins keep their current x/y; only this gap frame is created or expanded.
 */
export function placeDonePinInGapFrame(
  editor,
  activityId,
  doneFills,
  {
    doneKey = null,
    /** Optional display title (e.g. validation claim); defaults to doneKey. */
    frameLabel = null,
    board,
    savePlacements,
    saveFrames,
    saveSnapshot,
  } = {}
) {
  const id = String(activityId || '').trim();
  let key = String(doneKey || '').trim();
  if (!key && Array.isArray(doneFills)) {
    key = String(
      doneFills.find(entry => String(entry?.activityId || '').trim() === id)?.doneKey || ''
    ).trim();
  }
  if (!editor || !id || !key) return false;

  const shapeId = resolvePinShapeId(editor, id);
  if (!editor.getShape(shapeId)) return false;

  const siblings = siblingActivityIdsForDoneKey(doneFills, id, key);
  const memberIds = [...new Set([id, ...siblings])];
  const label = doneGapFrameLabel(frameLabel || key);
  const existing = findDoneGapFrame(editor, board, key)
    || (frameLabel && frameLabel !== key ? findDoneGapFrame(editor, board, frameLabel) : null);
  const frameId = existing?.id || doneGapFrameId(key);

  const placements = [];
  for (const memberId of memberIds) {
    const memberShapeId = resolvePinShapeId(editor, memberId);
    const shape = editor.getShape(memberShapeId);
    if (!shape || shape.type !== 'pin-card') continue;
    placements.push({
      activityId: memberId,
      x: shape.x,
      y: shape.y,
      width: shape.props?.w ?? DEFAULT_PIN_W,
      height: shape.props?.h ?? DEFAULT_PIN_H,
      frameId,
    });
  }
  if (!placements.length) return false;

  const seed = placements.find(p => p.activityId === id) || placements[0];
  const frame = fitFrameToMemberPlacements(
    {
      id: frameId,
      x: Number.isFinite(Number(existing?.x))
        ? Number(existing.x)
        : seed.x - DONE_GAP_FRAME_PADDING,
      y: Number.isFinite(Number(existing?.y))
        ? Number(existing.y)
        : seed.y - DONE_GAP_FRAME_PADDING,
      w: Number(existing?.w) || 0,
      h: Number(existing?.h) || 0,
      label,
    },
    placements
  );

  const before = extractBoardDocumentSnapshot(editor);
  editor.run(() => {
    editor.markHistoryStoppingPoint?.('before-done-gap-frame');
    createFrameShape(editor, frame);
    for (const placement of placements) {
      const pinId = resolvePinShapeId(editor, placement.activityId);
      const shape = editor.getShape(pinId);
      if (!shape || shape.type !== 'pin-card') continue;
      if ((shape.meta?.frameId || null) === frameId) continue;
      editor.updateShape({
        id: pinId,
        type: shape.type,
        meta: {
          ...shape.meta,
          frameId,
        },
      });
    }
    layerBoardContent(editor);
  });

  const nextFrames = mergeBoardFrames(collectBoardAndEditorFrames(editor, board), [frame]);
  saveFrames?.(nextFrames);
  const nextPlacements = mergePlacements(board?.placements || [], placements);
  savePlacements?.(nextPlacements);

  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      void flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* persist best-effort */
  }

  return true;
}
